import { useEffect, useRef, useState } from 'preact/hooks';

import type { WidgetApi } from '../api';
import { WidgetApiError } from '../api';
import { CheckIcon } from '../icons';
import { formatMoney } from '../lib/deposit-summary';
import { createSlotMount } from '../lib/slot-mount';
import { loadTurnstile } from '../lib/turnstile';
import type { ShopOrder, ShopProduct } from '../types';
import { ContactStep } from './Contact';
import { StripePaymentElement } from './StripePaymentElement';

/**
 * Phase 6 (D74) — buying a Special Service in the widget: details, pay,
 * done. No staff, no time, no cart: an order is its own thing on the
 * server, and the booking steps never see it.
 *
 * Card and Link only (D77). The order is immutable once created, so the
 * details form makes a NEW order when the details change, and the same
 * order (same key) when they do not — a retried tap never buys twice.
 * Analytics carry no product name and no value (D78); the conversion
 * value goes from the server.
 */

type Stage = 'details' | 'pay' | 'done';

interface Props {
  api: WidgetApi;
  sessionId: string;
  publishableKey: string;
  product: ShopProduct;
  /** The label the menu showed (program · option). */
  label: string;
  customViewBundle: string | null;
  prefilled?: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
  attribution: () => Record<string, unknown> | undefined;
  /** Generic, nameless analytics (D78). */
  report: (event: 'special_service_details' | 'special_service_purchase', orderId?: string) => void;
  onExit: () => void;
  onConfirmingChange?: (confirming: boolean) => void;
  /** Cloudflare Turnstile site key, when the salon has the check on. */
  turnstileSiteKey?: string;
}

function newKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function PurchaseFlow(props: Props) {
  const [stage, setStage] = useState<Stage>('details');
  const [order, setOrder] = useState<ShopOrder | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  // Stripe has charged the card; only our confirmation is outstanding.
  // From here Pay and Back are gone — the only action is to check again.
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The same details reuse the same key (and so the same order).
  const keyFor = useRef(new Map<string, string>());

  // Turnstile on the details step: rendered into the light DOM through a
  // slot (like Stripe's frame) so its iframe works inside the shadow root.
  const tsHolder = useRef<HTMLDivElement | null>(null);
  const tsToken = useRef<string | null>(null);
  const tsReset = useRef<(() => void) | null>(null);
  // The check failed or could not load: stop waiting for a token.
  const tsFailed = useRef(false);
  const siteKey = props.turnstileSiteKey;
  useEffect(() => {
    if (stage !== 'details' || !siteKey || !tsHolder.current) return;
    let cancelled = false;
    let id: string | null = null;
    let api: { remove(id: string): void } | null = null;
    tsFailed.current = false;
    const mount = createSlotMount(tsHolder.current, 'turnstile');
    void loadTurnstile()
      .then((ts) => {
        if (cancelled) return;
        api = ts;
        id = ts.render(mount.node, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: (t) => {
            tsToken.current = t;
            tsFailed.current = false;
          },
          'expired-callback': () => (tsToken.current = null),
          'error-callback': () => {
            tsToken.current = null;
            tsFailed.current = true;
          },
        });
        tsReset.current = () => {
          tsToken.current = null;
          tsFailed.current = false;
          if (id) ts.reset(id);
        };
      })
      .catch(() => {
        // The server decides whether a missing token is acceptable.
        tsFailed.current = true;
      });
    return () => {
      cancelled = true;
      tsReset.current = null;
      if (api && id) api.remove(id);
      mount.remove();
    };
  }, [stage, siteKey]);

  async function turnstileToken(): Promise<string | undefined> {
    if (!siteKey) return undefined;
    // The check usually finishes while the customer types; give it a moment.
    for (let i = 0; i < 40 && !tsToken.current && !tsFailed.current; i++) {
      await new Promise((r) => setTimeout(r, 200));
    }
    return tsToken.current ?? undefined;
  }

  async function submitDetails(info: { givenName: string; familyName?: string; email?: string; phone: string }) {
    if (!info.email) {
      setError('Please add your email — your receipt and next steps go there.');
      return;
    }
    setError(null);
    setBusy(true);
    const fingerprint = JSON.stringify([props.product.id, info.givenName, info.familyName, info.email, info.phone]);
    let orderKey = keyFor.current.get(fingerprint);
    if (!orderKey) {
      orderKey = newKey();
      keyFor.current.set(fingerprint, orderKey);
    }
    const turnstile = await turnstileToken();
    try {
      const { order: created } = await props.api.createOrder({
        orderKey,
        productId: props.product.id,
        ...(turnstile ? { turnstileToken: turnstile } : {}),
        client: { givenName: info.givenName, familyName: info.familyName ?? null, email: info.email, phone: info.phone },
        ...(props.attribution() ? { attribution: props.attribution() } : {}),
      });
      setOrder(created);
      setEmail(info.email);
      props.report('special_service_details', created.id);
      setStage(created.status === 'PAID' ? 'done' : 'pay');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      // A token is single-use: get a fresh one for the retry.
      tsReset.current?.();
    } finally {
      setBusy(false);
    }
  }

  function finish(paid: ShopOrder) {
    setOrder(paid);
    setStage('done');
    props.report('special_service_purchase', paid.id);
  }

  async function confirmPaid(orderId: string) {
    setPaid(true);
    setBusy(true);
    setError(null);
    // Stripe has already said yes by the time this runs, so the only
    // question is reaching our server. A network blip is retried; a
    // known refusal is shown as the server worded it; anything else
    // gets the safe message — never a raw error that might invite the
    // customer to pay again.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { order: paid } = await props.api.completeOrder(orderId);
        finish(paid);
        setBusy(false);
        return;
      } catch (e) {
        const code = e instanceof WidgetApiError ? e.code : null;
        if (code === 'PAYMENT_FAILED') {
          // Stripe holds no successful payment: unlock so they can retry.
          setPaid(false);
          setError((e as Error).message);
          setBusy(false);
          return;
        }
        if (code === 'PROVIDER_UNAVAILABLE' || code === 'PAYMENT_MISMATCH' || code === 'PAYMENT_PROCESSING') {
          // Money may have moved: stay locked; Check again is the only action.
          setError((e as Error).message);
          setBusy(false);
          return;
        }
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1500));
      }
    }
    setError(
      'Your payment may have gone through, but we could not confirm it just now. Please do not pay again — the salon will be in touch, or contact them.',
    );
    setBusy(false);
  }

  const banner = error && (
    <div class="sbw-banner-error" role="alert">
      <span aria-hidden="true" class="sbw-banner-error-icon">
        !
      </span>
      <span class="sbw-banner-error-text">{error}</span>
    </div>
  );

  if (stage === 'details') {
    return (
      <>
        {banner}
        <ContactStep
          onSubmit={(info) => void submitDetails(info)}
          submitting={busy}
          requireEmail
          requirePhone
          prefilled={props.prefilled}
          onBack={props.onExit}
          customViewBundle={props.customViewBundle}
          copy={{
            title: 'Your details.',
            sub: 'Where to send your receipt — and how our medical team will reach you.',
            fineprint: 'By continuing you agree to be contacted by the salon and its medical team about your order.',
          }}
        />
        {siteKey && <div ref={tsHolder} style={{ display: 'flex', justifyContent: 'center', padding: '0 16px' }} />}
      </>
    );
  }

  if (stage === 'pay' && order) {
    const line = order.lines[0];
    return (
      <>
        {banner}
        {paid && (
          <button
            type="button"
            class="sbw-btn sbw-btn--brand"
            style={{ margin: '0 0 12px' }}
            disabled={busy}
            onClick={() => void confirmPaid(order.id)}
          >
            {busy ? 'Checking…' : 'Check again'}
          </button>
        )}
        <StripePaymentElement
          sessionId={props.sessionId}
          publishableKey={props.publishableKey}
          amountCents={order.totalCents}
          currency={order.currency}
          // Card only, matching the intent the server creates (D77).
          paymentMethodTypes={['card']}
          // Once paid, the form stays locked: a second Pay would charge again.
          submitting={busy || paid}
          onConfirmingChange={props.onConfirmingChange}
          header={
            <>
              <div class="sbw-eyebrow">Your order</div>
              <h2 class="sbw-step-title">Complete your purchase.</h2>
              <p class="sbw-step-sub">No appointment needed — our medical team takes it from here.</p>
              <div class="sbw-deposit-card">
                <div class="sbw-deposit-row">
                  <span>{props.label}</span>
                  <span class="sbw-amt">{formatMoney(order.subtotalCents, order.currency)}</span>
                </div>
                {order.taxCents > 0 && (
                  <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)' }}>
                    <span>Sales tax</span>
                    <span class="sbw-amt">{formatMoney(order.taxCents, order.currency)}</span>
                  </div>
                )}
                <div class="sbw-deposit-row is-emph">
                  <span>Pay today</span>
                  <span class="sbw-amt">{formatMoney(order.totalCents, order.currency)}</span>
                </div>
              </div>
              {line && line.quantity > 1 && <p class="sbw-step-sub">Quantity: {line.quantity}</p>}
            </>
          }
          onCreateIntent={async () => {
            const r = await props.api.createOrderIntent(order.id);
            if ('paid' in r) {
              // Already paid (a retry after a lost response): finish, and
              // stop this attempt before it can confirm anything.
              finish(r.order);
              throw new Error('This order is already paid.');
            }
            return { clientSecret: r.clientSecret };
          }}
          onPaid={() => void confirmPaid(order.id)}
          {...(paid ? {} : { onBack: () => setStage('details') })}
        />
      </>
    );
  }

  // Done.
  return (
    <div class="sbw-done-stage">
      <div class="sbw-done-mark">
        <CheckIcon size={28} />
      </div>
      <div class="sbw-done-eyebrow">Order received</div>
      <h2 class="sbw-done-title">Thank you.</h2>
      <p class="sbw-done-sub">Our medical team will reach out within 1 business day.</p>
      {order && (
        <p class="sbw-confirmation-copy">
          {props.label} · {formatMoney(order.totalCents, order.currency)} paid.
          {email ? ` A receipt is on its way to ${email}.` : ''}
        </p>
      )}
      {order && <div class="sbw-done-id">ORDER · {order.id.slice(0, 8).toUpperCase()}</div>}
    </div>
  );
}
